/**
 * User Type Utilities — single source of truth for roles, menus, and route ACL.
 * Coarse types map to product shells (ADR 016). Fine-grained org roles live on memberships.
 */

export const USER_TYPES = {
  CANDIDATE: 'candidate',
  CONSULTANCY: 'consultancy',
  INSTITUTION: 'institution',
  PARTNER: 'partner',
  ADMIN: 'admin',
};

export const USER_TYPE_LABELS = {
  [USER_TYPES.CANDIDATE]: 'Candidate / Student',
  [USER_TYPES.CONSULTANCY]: 'Consultancy',
  [USER_TYPES.INSTITUTION]: 'Institution',
  [USER_TYPES.PARTNER]: 'EdTech partner',
  [USER_TYPES.ADMIN]: 'Administrator',
};

export const USER_TYPE_COLORS = {
  [USER_TYPES.CANDIDATE]: '#0D9488',
  [USER_TYPES.CONSULTANCY]: '#EA580C',
  [USER_TYPES.INSTITUTION]: '#0284C7',
  [USER_TYPES.PARTNER]: '#7C3AED',
  [USER_TYPES.ADMIN]: '#0F766E',
};

const C = USER_TYPES.CANDIDATE;
const K = USER_TYPES.CONSULTANCY;
const I = USER_TYPES.INSTITUTION;
const P = USER_TYPES.PARTNER;
const A = USER_TYPES.ADMIN;

/** Slug → path (sidebar + ACL helpers) */
export const PATH_BY_SLUG = {
  dashboard: '/dashboard',
  'admin-dashboard': '/admin/dashboard',
  'mock-interviews': '/mock-interviews',
  'interview-modes': '/interview-modes',
  preparation: '/preparation',
  'career-path': '/career-path',
  'job-matching': '/job-matching',
  performance: '/performance',
  'skill-assessment': '/skill-assessment',
  portfolio: '/portfolio',
  subscriptions: '/subscriptions',
  support: '/support',
  'job-descriptions': '/job-descriptions',
  tutorials: '/tutorials',
  partners: '/partners',
  'partner-dashboard': '/partner/dashboard',
  'admin-partners': '/admin/partners',
  'admin-sales': '/admin/sales',
  'admin-wesenseu': '/admin/wesenseu',
  'admin-wesenseu-connection': '/admin/wesenseu-connection',
  'admin-crm': '/admin/crm',
  'admin-staff': '/admin/staff',
  'admin-test-manager': '/admin/test-manager',
  'talent-discovery': '/talent-discovery',
  'candidate-management': '/candidate-management',
  'crm-pipeline': '/crm',
  'consultancy-team': '/team',
  'client-companies': '/client-companies',
  openings: '/openings',
  cobroker: '/cobroker',
  replacements: '/replacements',
  'interview-scheduling': '/interview-scheduling',
  'bulk-operations': '/bulk-operations',
  invites: '/invites',
  'placement-analytics': '/placement-analytics',
  'crm-analytics': '/placement-analytics',
  'candidate-reports': '/candidate-reports',
  'consultancy-ops': '/consultancy-ops',
  'business-intelligence': '/business-intelligence',
  'crm-overview': '/business-intelligence',
  'subscription-billing': '/subscription-billing',
  'user-management': '/admin/users',
  'subscription-management': '/admin/subscriptions',
  'platform-analytics': '/admin/analytics',
  'interview-hierarchy': '/admin/interview-hierarchy',
  'content-management': '/admin/content',
  'system-monitoring': '/admin/monitoring',
  'knowledge-catalog': '/admin/knowledge',
  'business-metrics': '/admin/monitoring',
  'security-audit': '/admin/security',
  'api-management': '/admin/api',
  'support-tickets': '/admin/support',
  'payment-gateways': '/admin/payment-gateways',
  'system-settings': '/system-settings',
  profile: '/profile',
  interviews: '/interviews',
  analytics: '/analytics',
  'help-support': '/help-support',
  settings: '/settings',
  'admin-panel': '/admin/panel',
  'cohort-learners': '/institution/learners',
  'training-reports': '/institution/reports',
  'institution-interviews': '/institution/interviews',
  'institution-plans': '/institution/plans',
  'institution-profile': '/institution/profile',
  'institution-team': '/institution/team',
  'placement-drives': '/institution/drives',
  'institution-trainings': '/institution/trainings',
  'my-trainings': '/my-trainings',
  curriculum: '/profile/curriculum',
  'institution-curriculum': '/institution/curriculum',
  'admin-institution-trainings': '/admin/institution-trainings',
  'admin-training-advertise': '/admin/training-advertise',
  'partner-team': '/partner/team',
  'interview-planner': '/interview-plans/new',
  'interview-history': '/interview-history',
  'review-answers': '/review-answers',
};

export const getPathForSlug = (slug, user = null) => {
  if (slug === 'dashboard' && user && getUserType(user) === A) {
    return PATH_BY_SLUG['admin-dashboard'];
  }
  return PATH_BY_SLUG[slug] || '/dashboard';
};

/** Primary sidebar menus (shipped product surface) */
const CANDIDATE_MENU = [
  { slug: 'dashboard', title: 'Dashboard', icon: 'FiHome' },
  { slug: 'invites', title: 'Invites', icon: 'FiMessageSquare' },
  { slug: 'mock-interviews', title: 'Mock Interviews', icon: 'FiVideo' },
  { slug: 'interview-planner', title: 'Plan from a JD', icon: 'FiLayers' },
  { slug: 'interview-history', title: 'Interviews & reports', icon: 'FiBarChart2' },
  { slug: 'review-answers', title: 'Review answers', icon: 'FiClipboard' },
  { slug: 'curriculum', title: 'Curriculum', icon: 'FiBookOpen' },
  { slug: 'interview-modes', title: 'Interview Modes', icon: 'FiTarget' },
  { slug: 'job-descriptions', title: 'Job Descriptions', icon: 'FiFileText' },
  { slug: 'subscriptions', title: 'Subscription Plans', icon: 'FiCreditCard' },
  { slug: 'tutorials', title: 'Tutorials', icon: 'FiBookOpen' },
  { slug: 'my-trainings', title: 'My trainings', icon: 'FiVideo' },
  { slug: 'support', title: 'Help & Support', icon: 'FiMessageSquare' },
];

const CONSULTANCY_MENU = [
  { slug: 'dashboard', title: 'Dashboard', icon: 'FiHome' },
  { slug: 'invites', title: 'Invites', icon: 'FiMessageSquare' },
  { slug: 'crm-pipeline', title: 'CRM Pipeline', icon: 'FiLayers' },
  { slug: 'candidate-management', title: 'Candidates', icon: 'FiUsers' },
  { slug: 'bulk-operations', title: 'Bulk invite', icon: 'FiUpload' },
  { slug: 'candidate-reports', title: 'Interview reports', icon: 'FiBarChart2' },
  { slug: 'consultancy-team', title: 'CRM Team', icon: 'FiUsers' },
  { slug: 'talent-discovery', title: 'Talent Discovery', icon: 'FiUsers' },
  { slug: 'client-companies', title: 'Client Companies', icon: 'FiHome' },
  { slug: 'openings', title: 'Openings', icon: 'FiBriefcase' },
  { slug: 'cobroker', title: 'Co-broker board', icon: 'FiLayers' },
  { slug: 'replacements', title: 'Replacement escrow', icon: 'FiBriefcase' },
  { slug: 'interview-scheduling', title: 'Interview Scheduling', icon: 'FiCalendar' },
  { slug: 'business-intelligence', title: 'Business Intelligence', icon: 'FiDatabase' },
  { slug: 'consultancy-ops', title: 'Ops · EMS & GST', icon: 'FiBriefcase' },
  { slug: 'placement-analytics', title: 'CRM Analytics', icon: 'FiBarChart2' },
  { slug: 'subscription-billing', title: 'Subscription & Billing', icon: 'FiCreditCard' },
  { slug: 'tutorials', title: 'Tutorials', icon: 'FiBookOpen' },
  { slug: 'support', title: 'Support', icon: 'FiMessageSquare' },
];

const INSTITUTION_MENU = [
  { slug: 'dashboard', title: 'Dashboard', icon: 'FiHome' },
  { slug: 'invites', title: 'Invites', icon: 'FiMessageSquare' },
  { slug: 'cohort-learners', title: 'Students', icon: 'FiUsers' },
  { slug: 'institution-curriculum', title: 'Curriculum', icon: 'FiBookOpen' },
  { slug: 'institution-team', title: 'Staff team', icon: 'FiUsers' },
  { slug: 'institution-profile', title: 'Institution profile', icon: 'FiUser' },
  { slug: 'institution-plans', title: 'Interview plans', icon: 'FiCreditCard' },
  { slug: 'placement-drives', title: 'Placement drives', icon: 'FiBriefcase' },
  { slug: 'institution-trainings', title: 'Training requests', icon: 'FiCalendar' },
  { slug: 'subscriptions', title: 'Talent packages', icon: 'FiCreditCard' },
  { slug: 'institution-interviews', title: 'Training Interviews', icon: 'FiCalendar' },
  { slug: 'training-reports', title: 'Interview reports', icon: 'FiBarChart2' },
  { slug: 'tutorials', title: 'Tutorials', icon: 'FiBookOpen' },
  { slug: 'support', title: 'Support', icon: 'FiMessageSquare' },
];

/** Super admin application control center (includes CRM oversight; day-to-day CRM is consultancy-owned). */
const ADMIN_OPS = [
  { slug: 'admin-dashboard', title: 'Control center', icon: 'FiHome' },
  { slug: 'admin-sales', title: 'Sales & onboarding', icon: 'FiCreditCard' },
  { slug: 'admin-test-manager', title: 'Test manager', icon: 'FiClipboard' },
  { slug: 'admin-crm', title: 'CRM desk', icon: 'FiLayers' },
  { slug: 'user-management', title: 'Users', icon: 'FiUsers' },
  { slug: 'admin-staff', title: 'Admin staff', icon: 'FiShield' },
  { slug: 'knowledge-catalog', title: 'Knowledge catalog', icon: 'FiLayers' },
  { slug: 'content-management', title: 'Content', icon: 'FiEdit3' },
  { slug: 'interview-hierarchy', title: 'Interview tree', icon: 'FiFolder' },
  { slug: 'subscription-management', title: 'Plans', icon: 'FiCreditCard' },
  { slug: 'support-tickets', title: 'Support', icon: 'FiMessageSquare' },
  { slug: 'system-monitoring', title: 'Health', icon: 'FiActivity' },
  { slug: 'payment-gateways', title: 'Payments', icon: 'FiCreditCard' },
  { slug: 'system-settings', title: 'System settings', icon: 'FiSettings' },
  { slug: 'admin-partners', title: 'EdTech partners', icon: 'FiBookOpen' },
  { slug: 'admin-institution-trainings', title: 'Institution training', icon: 'FiCalendar' },
  { slug: 'admin-training-advertise', title: 'Advertise training', icon: 'FiCalendar' },
  { slug: 'admin-wesenseu-connection', title: 'WeSenseU connection', icon: 'FiSettings' },
  { slug: 'admin-wesenseu', title: 'WeSenseU analysis', icon: 'FiActivity' },
];

/** Limited sales_team menu — partner wallets + inquiries only */
const ADMIN_SALES_MENU = [
  { slug: 'admin-sales', title: 'Sales & onboarding', icon: 'FiCreditCard' },
];

const PARTNER_MENU = [
  { slug: 'partner-dashboard', title: 'Partner catalog', icon: 'FiBookOpen' },
  { slug: 'partner-team', title: 'Staff team', icon: 'FiUsers' },
  { slug: 'invites', title: 'Invites', icon: 'FiMessageSquare' },
  { slug: 'partners', title: 'Public partners page', icon: 'FiLayers' },
  { slug: 'tutorials', title: 'Tutorials', icon: 'FiBookOpen' },
  { slug: 'profile', title: 'Profile', icon: 'FiUser' },
  { slug: 'settings', title: 'Settings', icon: 'FiSettings' },
  { slug: 'support', title: 'Support', icon: 'FiMessageSquare' },
];

const uniqueBySlug = (items) => {
  const seen = new Set();
  return items.filter((item) => {
    if (seen.has(item.slug)) return false;
    seen.add(item.slug);
    return true;
  });
};

const ADMIN_MENU = uniqueBySlug(ADMIN_OPS);

/** Extra dropdown/mobile shortcuts (not in primary sidebar) */
const CANDIDATE_QUICK = [
  { slug: 'skill-assessment', title: 'Skill Assessment', icon: 'FiAward' },
  { slug: 'career-path', title: 'Career Path', icon: 'FiTrendingUp', comingSoon: true },
  { slug: 'job-matching', title: 'Job Matching', icon: 'FiBriefcase', comingSoon: true },
];

const CONSULTANCY_QUICK = [
  { slug: 'openings', title: 'Openings', icon: 'FiBriefcase' },
];

export const getUserType = (user) => {
  if (!user) return C;
  const raw = (user.user_type || user.profile?.user_type || '').toLowerCase();
  if (raw === 'student' || raw === 'learner') return C;
  if (raw) return raw;
  if (user.is_staff || user.is_superuser) return A;
  return C;
};

export const isUserType = (user, userType) =>
  getUserType(user) === String(userType).toLowerCase();

export const isCandidate = (user) => isUserType(user, C);
export const isConsultancy = (user) => isUserType(user, K);
export const isInstitution = (user) => isUserType(user, I);
export const isPartner = (user) => isUserType(user, P);
export const isAdmin = (user) => isUserType(user, A);

export const getUserTypeLabel = (user) =>
  USER_TYPE_LABELS[getUserType(user)] || USER_TYPE_LABELS[C];

export const getUserTypeColor = (user) =>
  USER_TYPE_COLORS[getUserType(user)] || USER_TYPE_COLORS[C];

export const getDashboardComponent = (user) => {
  switch (getUserType(user)) {
    case A:
      return 'AdminDashboard';
    case K:
      return 'ConsultancyDashboard';
    case I:
      return 'InstitutionDashboard';
    case P:
      return 'PartnerDashboard';
    default:
      return 'CandidateDashboard';
  }
};

export const getHomePathForUser = (user) => {
  switch (getUserType(user)) {
    case A:
      if (user?.admin_role === 'sales_team' || user?.is_sales_limited) {
        return '/admin/sales';
      }
      return '/admin/dashboard';
    case P:
      return '/partner/dashboard';
    case K:
    case I:
    default:
      return '/dashboard';
  }
};

const withPaths = (items, user) =>
  items.map((item) => ({
    ...item,
    path: getPathForSlug(item.slug, user),
  }));

export const getMenuForUserType = (user) => {
  const userType = getUserType(user);
  switch (userType) {
    case A:
      if (user?.admin_role === 'sales_team' || user?.is_sales_limited) {
        return withPaths(ADMIN_SALES_MENU, user);
      }
      return withPaths(ADMIN_MENU, user);
    case K:
      return withPaths(CONSULTANCY_MENU, user);
    case I:
      return withPaths(INSTITUTION_MENU, user);
    case P:
      return withPaths(PARTNER_MENU, user);
    default:
      return withPaths(CANDIDATE_MENU, user);
  }
};

export const getQuickActionsForUserType = (user) => {
  const userType = getUserType(user);
  let items;
  switch (userType) {
    case A:
      items = ADMIN_MENU.slice(0, 4);
      break;
    case K:
      items = [...CONSULTANCY_MENU.slice(1, 5), ...CONSULTANCY_QUICK];
      break;
    case I:
      items = INSTITUTION_MENU.slice(1, 4);
      break;
    case P:
      items = PARTNER_MENU.slice(0, 3);
      break;
    default:
      items = [...CANDIDATE_MENU.slice(1, 4), ...CANDIDATE_QUICK];
  }
  // Never surface comingSoon placeholders as clickable quick actions
  return withPaths(items.filter((a) => !a.comingSoon), user);
};

/**
 * Feature / route ACL map. Unknown features deny access.
 */
export const FEATURE_ACCESS = {
  'user-management': [A],
  'subscription-management': [A],
  'platform-analytics': [A],
  'interview-hierarchy': [A],
  'content-management': [A],
  'system-monitoring': [A],
  'business-metrics': [A],
  'security-audit': [A],
  'api-management': [A],
  'admin-dashboard': [A],
  'knowledge-catalog': [A],
  'payment-gateways': [A],
  'system-settings': [A],
  'admin-partners': [A],
  'admin-institution-trainings': [A],
  'admin-training-advertise': [A],
  'admin-sales': [A],
  'admin-wesenseu': [A],
  'admin-wesenseu-connection': [A],
  'admin-crm': [A],
  'admin-staff': [A],
  'admin-test-manager': [A],
  'partner-dashboard': [P, A],
  partners: [C, K, I, P, A],
  'talent-discovery': [K, A],
  curriculum: [C, A],
  'institution-curriculum': [I, A],
  'candidate-management': [K, A],
  'crm-pipeline': [K, A],
  'consultancy-team': [K, A],
  'interview-scheduling': [K, A],
  'client-companies': [K, A],
  openings: [K, A],
  cobroker: [K, A],
  replacements: [K, A],
  'placement-analytics': [K, A],
  'candidate-reports': [K, A],
  'consultancy-ops': [K, A],
  'bulk-operations': [K, A],
  'business-intelligence': [K, A],
  'crm-overview': [K, A],
  'subscription-billing': [K, A],
  'cohort-learners': [I, A],
  'training-reports': [I, A],
  'institution-interviews': [I, A],
  'institution-plans': [I, A],
  'institution-profile': [I, A],
  'institution-team': [I, A],
  'placement-drives': [I, A],
  'institution-trainings': [I, A],
  'my-trainings': [C],
  'partner-team': [P, A],
  'interview-planner': [C],
  'interview-history': [C],
  'review-answers': [C],
  invites: [C, K, I, P, A],
  'mock-interviews': [C],
  'interview-modes': [C],
  'skill-assessment': [C],
  'career-path': [C],
  'job-matching': [C],
  performance: [C],
  preparation: [C],
  'job-descriptions': [C],
  subscriptions: [C, K, I, P, A],
  interviews: [C],
  analytics: [C, K, I, A],
  profile: [C, K, I, P, A],
  settings: [C, K, I, P, A],
  portfolio: [C, K, I, A],
  support: [C, K, I, P, A],
  'help-support': [C, K, I, P, A],
  tutorials: [C, K, I, P, A],
  dashboard: [C, K, I, P, A],
};

export const hasFeatureAccess = (user, feature) => {
  const userType = getUserType(user);
  if (userType === A && (FEATURE_ACCESS[feature] || PATH_BY_SLUG[feature])) return true;
  const allowed = FEATURE_ACCESS[feature];
  if (!allowed) return false;
  return allowed.includes(userType);
};

export const userHasAllowedType = (user, allowedUserTypes) => {
  if (!allowedUserTypes || allowedUserTypes.length === 0) return true;
  if (getUserType(user) === A) return true;
  return allowedUserTypes.includes(getUserType(user));
};

export const getUserTypeBadgeProps = (user) => ({
  label: getUserTypeLabel(user),
  color: getUserTypeColor(user),
  userType: getUserType(user),
});

export default {
  USER_TYPES,
  USER_TYPE_LABELS,
  USER_TYPE_COLORS,
  PATH_BY_SLUG,
  getPathForSlug,
  getUserType,
  isUserType,
  isCandidate,
  isConsultancy,
  isInstitution,
  isAdmin,
  getUserTypeLabel,
  getUserTypeColor,
  getDashboardComponent,
  getHomePathForUser,
  getMenuForUserType,
  getQuickActionsForUserType,
  hasFeatureAccess,
  userHasAllowedType,
  getUserTypeBadgeProps,
  FEATURE_ACCESS,
};
